import { AxiosError } from 'axios';
import { QueryKey, useInfiniteQuery, UseInfiniteQueryOptions, useMutation, useQuery } from 'react-query';
import { OPPORTUNITIES_QUERY_STRING } from 'shared/constants/QUERY_STRING';
import { talentDao } from 'shared/dao/talentDao';
import { ICommonResponsePayload } from 'shared/interfaces/ICommon';
import { ITalentMembersResponse, ITalentProfileIdsResponse, ITalentProfilesResponse } from 'shared/interfaces/IProfile';
import {
  ICreateGuardianPayload,
  ICreateTalentPayload,
  IMessagePayload,
  IProfessionsResponsePayload,
  ISenderInfoResponsePayload,
  ISpecificTalentResponsePayload,
  ITalentBiographyResponsePayload,
  ITalentCalendarEventAggregatesPayload,
  ITalentCalendarEventAggregatesResponse,
  ITalentCalendarEventPayload,
  ITalentCalendarEventsPayload,
  ITalentCalendarEventsResponse,
  ITalentGeneralResponsePayload,
  ITalentMeResponsePayload,
  ITalentResumeResponsePayload,
  ITalentSkillResponsePayload,
  ITalentStatisticsResponsePayload,
  ITalentUpdatePayload,
} from 'shared/interfaces/ITalent';
// import { ITalentUpdatePayload } from 'shared/interfaces/ITalent';
import { IAxiosErrorResponse } from 'shared/interfaces/utils/IAxios';
import { IErrorResponse } from 'shared/interfaces/utils/IErrorResonse';

const {
  updateTalent: updateTalentDao,
  getStatistics: getStatisticsDao,
  getResume: getResumeDao,
  getSkills: getSkillsDao,
  getGeneral: getGeneralDao,
  getMe: getMeDao,
  createGuardian: createGuardianDao,
  createTalent: createTalentDao,
  getTalentMembers: getTalentMembersDao,
  getTalentProfiles: getTalentProfilesDao,
  impersonateTalent: impersonateTalentDao,
  updateMe: updateMeDao,
  getBiography: getBiographyDao,
  getProfessions: getProfessionsDao,
  getMyData: getMyDataDao,
  getSenderDetails: getSenderDetailsDao,
  getTalentProfileIds: getTalentProfileIdsDao,
  getCalendarAggregates: getCalendarAggregatesDao,
  getTalentCalendarEvents: getTalentCalendarEventsDao,
  createCalendarEvent: createCalendarEventDao,
  updateCalendarEvent: updateCalendarEventDao,
  deleteCalendarEvent: deleteCalendarEventDao,
  sendMessage: sendMessageDao,
} = talentDao();

export const talentService = () => {
  const updateMe = () => {
    return useMutation((payload: ITalentUpdatePayload) => updateMeDao(payload));
  };
  const updateTalent = () => {
    return useMutation((payload: ITalentUpdatePayload) => updateTalentDao(payload));
  };

  const createGuardian = () => {
    return useMutation((payload: ICreateGuardianPayload) => createGuardianDao(payload));
  };

  const getStatistics = () => {
    return useQuery<ITalentStatisticsResponsePayload>(['talents/statistics'], () => getStatisticsDao());
  };

  const getResume = () => {
    return useQuery<ITalentResumeResponsePayload>(['talents/resume'], () => getResumeDao());
  };

  const getSkills = () => {
    return useQuery<ITalentSkillResponsePayload>(['talents/skills'], () => getSkillsDao());
  };

  const getGeneral = (enabled?: boolean) => {
    return useQuery<ITalentGeneralResponsePayload>(['talents/general'], () => getGeneralDao(), {
      enabled: enabled || true,
    });
  };

  const getMe = () => {
    return useQuery<ITalentMeResponsePayload>(['me'], () => getMeDao());
  };

  const createTalent = () => {
    return useMutation((payload: ICreateTalentPayload) => createTalentDao(payload));
  };

  const getTalentMembers = (searchString: string, token: string) => {
    return useQuery<ITalentMembersResponse, IAxiosErrorResponse>(
      ['talent/members', searchString, token],
      () => getTalentMembersDao(searchString, token),
      {
        enabled: Boolean(token),
      },
    );
  };

  const getTalentProfiles = (searchString: string, enabled?: string) => {
    console.log('getTalentProfiles', enabled ? enabled === 'true' : true, enabled);

    return useQuery<ITalentProfilesResponse, IAxiosErrorResponse>(
      ['talent/profiles', searchString],
      () => getTalentProfilesDao(searchString),
      {
        enabled: enabled ? enabled === 'true' : true,
      },
    );
  };

  const getTalentProfileIds = (enabled: boolean) => {
    return useQuery<ITalentProfileIdsResponse, IAxiosErrorResponse>(
      [OPPORTUNITIES_QUERY_STRING.OPPORTUNITIES_PROFILES],
      () => getTalentProfileIdsDao(),
      {
        enabled,
      },
    );
  };

  const impersonateTalent = () => {
    return useMutation((userId: string) => impersonateTalentDao(userId));
  };

  const getBiography = () => {
    return useQuery<ITalentBiographyResponsePayload>(['talents/biography'], () => getBiographyDao());
  };

  const getProfessions = (searchString?: string) => {
    return useQuery<IProfessionsResponsePayload>(['professions', searchString], () => getProfessionsDao(searchString));
  };

  const getSenderDetails = (id: string, type: 'Talent' | 'Company') => {
    return useQuery<ISenderInfoResponsePayload>(['sender_details', id, type], () => getSenderDetailsDao(id, type));
  };

  const sendMessage = () => {
    return useMutation((payload: IMessagePayload) => sendMessageDao(payload));
  };

  const getMyData = (talentId: string) => {
    return useQuery<ISpecificTalentResponsePayload, Error>(['my_data', talentId], () => getMyDataDao(talentId));
  };

  const getCalendarAggregates = (
    payload: ITalentCalendarEventAggregatesPayload,
    options?: Omit<
      UseInfiniteQueryOptions<
        ITalentCalendarEventAggregatesResponse,
        IAxiosErrorResponse,
        ITalentCalendarEventAggregatesResponse,
        QueryKey
      >,
      'queryKey' | 'queryFn'
    >,
  ) => {
    return useQuery<ITalentCalendarEventAggregatesResponse>(
      ['calendar_aggregate', payload],
      () => getCalendarAggregatesDao(payload),
      {
        enabled: options?.enabled,
      },
    );
  };

  const getTalentCalendarEvents = (
    payload: ITalentCalendarEventsPayload,
    query?: string,
    options?: Omit<
      UseInfiniteQueryOptions<
        ITalentCalendarEventsResponse,
        IAxiosErrorResponse,
        ITalentCalendarEventsResponse,
        QueryKey
      >,
      'queryKey' | 'queryFn'
    >,
  ) => {
    return useInfiniteQuery<ITalentCalendarEventsResponse, IAxiosErrorResponse, ITalentCalendarEventsResponse>(
      ['talent_calendar_events', payload],
      ({ pageParam = '' }) => getTalentCalendarEventsDao({ ...payload, after: pageParam }, query || ''),
      {
        getNextPageParam: (lastPage) => {
          return lastPage.data.calendar_events_connection.pageInfo.hasNextPage
            ? lastPage.data.calendar_events_connection.pageInfo.endCursor
            : undefined;
        },
        enabled: options?.enabled,
      },
    );
  };

  const createCalendarEvent = () => {
    return useMutation<{}, AxiosError<IErrorResponse>, ITalentCalendarEventPayload>(
      (payload: ITalentCalendarEventPayload) => createCalendarEventDao(payload),
    );
  };

  const updateCalendarEvent = () => {
    return useMutation<{}, AxiosError<IErrorResponse>, ITalentCalendarEventPayload>(
      (payload: ITalentCalendarEventPayload) => updateCalendarEventDao(payload),
    );
  };

  const deleteCalendarEvent = () => {
    return useMutation<ICommonResponsePayload, IAxiosErrorResponse, { id: string; talentId: string }>(
      ({ id, talentId }) => deleteCalendarEventDao(id, talentId),
    );
  };

  return {
    sendMessage,
    getTalentProfileIds,
    getSenderDetails,
    updateMe,
    impersonateTalent,
    getTalentProfiles,
    getTalentMembers,
    getMe,
    createTalent,
    createGuardian,
    getSkills,
    getResume,
    updateTalent,
    getStatistics,
    getBiography,
    getProfessions,
    getGeneral,
    getMyData,
    getCalendarAggregates,
    getTalentCalendarEvents,
    createCalendarEvent,
    updateCalendarEvent,
    deleteCalendarEvent,
  };
};
